import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { IntroNote } from "@/components/IntroNote";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Inbox, Mail, Building2, RefreshCw, Flame, Filter, CheckCircle2, AlertTriangle, Plug, Rocket, Copy, X } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useCollapsible } from "@/components/CollapsibleList";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const STATUS_STYLE = {
  new: "bg-ek-purple/10 text-ek-purple",
  contacted: "bg-blue-100 text-blue-700",
  qualified: "bg-amber-100 text-amber-700",
  won: "bg-green-100 text-green-700",
  lost: "bg-gray-100 text-gray-500",
};

const BAND_STYLE = {
  HOT: "bg-rose-100 text-rose-800 border-rose-200",
  WARM: "bg-amber-100 text-amber-800 border-amber-200",
  TEPID: "bg-sky-100 text-sky-800 border-sky-200",
  COLD: "bg-slate-100 text-slate-600 border-slate-200",
};
const BAND_DOT = {
  HOT: "bg-rose-500",
  WARM: "bg-amber-500",
  TEPID: "bg-sky-400",
  COLD: "bg-slate-400",
};

const gbp = (x) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(x || 0);

/**
 * Build a `mailto:` link that opens the Owner's email client pre-filled with a
 * warm reply quoting the lead's own ROI numbers — closes the loop between the
 * passive signal and the first outreach in one click. No backend round-trip
 * (`mailto:` honours the user's default client on every platform).
 */
function buildRoiReplyMailto(lead) {
  const sig = lead.roi_signal || {};
  const firstName = (lead.name || "").trim().split(/\s+/)[0] || "there";
  const src = sig.source === "one-pager" ? "downloaded our ROI one-pager" : "ran a £1m+ ROI estimate";
  const subject = `Quick follow-up — ERIO ROI for ${lead.company || "your team"} (${gbp(sig.annual_value)} estimated)`;
  const body = [
    `Hi ${firstName},`,
    "",
    `Thanks for getting in touch about ERIO — I saw you ${src} on your own figures (${gbp(sig.revenue)} revenue · ${(sig.suppliers || 0).toLocaleString()} suppliers · ${sig.incidents} material incidents/yr), which works out at roughly ${gbp(sig.annual_value)} of annual loss-avoidance + efficiency value.`,
    "",
    "Happy to walk through how we'd validate that number against your live exposure and put a 4-week pilot plan together. Are you free for a 25-min call this week or next?",
    "",
    "Best,",
    "",
    "EKSELENS · ERIO",
    "https://erio.ekselens.com",
  ].join("\n");
  return `mailto:${encodeURIComponent(lead.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function Leads() {
  const [data, setData] = useState({ leads: [], total: 0, counts: {}, bands: {}, locale_counts: {} });
  const [loading, setLoading] = useState(true);
  const [bandFilter, setBandFilter] = useState("ALL");
  const [sourceFilter, setSourceFilter] = useState("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/leads");
      setData(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load leads.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (lead_id, status) => {
    try {
      await api.patch(`/leads/${lead_id}`, { status });
      setData((d) => ({
        ...d,
        leads: d.leads.map((l) => (l.lead_id === lead_id ? { ...l, status } : l)),
      }));
      toast.success(`Lead marked ${status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed.");
    }
  };

  const provisionFromLead = async (lead_id, opts = {}) => {
    try {
      const res = await api.post(`/owner/leads/${lead_id}/provision-tenant`, {
        send_welcome_email: !!opts.sendWelcomeEmail,
      });
      setData((d) => ({
        ...d,
        leads: d.leads.map((l) => (
          l.lead_id === lead_id
            ? { ...l, tenant_provisioned: res.data.lead.tenant_provisioned, status: "qualified" }
            : l
        )),
      }));
      const we = res.data.welcome_email;
      if (we?.sent) {
        toast.success(`Tenant ${res.data.tenant_id} provisioned & welcome email sent`);
      } else if (opts.sendWelcomeEmail) {
        toast.warning(
          `Tenant ${res.data.tenant_id} provisioned, but welcome email failed: ${we?.error || we?.reason}`
        );
      } else {
        toast.success(`Tenant ${res.data.tenant_id} provisioned`);
      }
      return res.data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Provisioning failed.");
      throw err;
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-8" data-testid="leads-page">
      <Seo title="Leads" description="Demo and brochure leads captured from the ERIO marketing surface." path="/leads" />
      <div className="flex items-center justify-between">
        <div>
          <p className="ek-eyebrow">Owner · CRM</p>
          <h1 className="ek-heading mt-1 text-3xl">Leads</h1>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="leads-refresh"
          className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-medium text-ek-ink transition-colors hover:bg-ek-purple/5"
        >
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      <IntroNote className="mt-4">
        Demo &amp; brochure requests captured from the public &ldquo;How it works&rdquo; page and the login screen.
        Every lead also lands in the Owner inbox via email. Update the pipeline status as you work each one.
      </IntroNote>

      {/* Summary tiles — Intent bands lead, since they're the prioritisation signal */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="leads-intent-summary">
        {["HOT", "WARM", "TEPID", "COLD"].map((b) => {
          const active = bandFilter === b;
          const count = data.bands?.[b] || 0;
          return (
            <button
              key={b}
              type="button"
              onClick={() => setBandFilter(active ? "ALL" : b)}
              data-testid={`leads-band-${b.toLowerCase()}-tile`}
              className={`text-left rounded-xl border p-4 transition ${active ? "ring-2 ring-ek-purple border-transparent" : ""} ${BAND_STYLE[b]} hover:scale-[1.02]`}
            >
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                <span className={`inline-block h-2 w-2 rounded-full ${BAND_DOT[b]}`} /> {b}
              </p>
              <p className="font-display text-2xl font-bold">{count}</p>
              <p className="text-[0.65rem] opacity-70 mt-0.5">
                {b === "HOT" ? "act today" : b === "WARM" ? "act this week" : b === "TEPID" ? "nurture" : "monitor"}
              </p>
            </button>
          );
        })}
        <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4">
          <p className="flex items-center gap-1 text-xs text-amber-700"><Flame size={12} /> PDF-warmed</p>
          <p className="font-display text-2xl font-bold text-amber-700" data-testid="leads-warmed">{data.warmed || 0}</p>
          <p className="text-[0.65rem] text-amber-700/70 mt-0.5">ran ROI calc</p>
        </div>
      </div>

      {/* Pipeline counters (secondary) */}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-ek-ink/65" data-testid="leads-summary">
        <span><strong className="text-ek-ink">{data.total}</strong> total</span>
        {STATUSES.map((s) => (
          <span key={s} className="capitalize">· <strong className="text-ek-ink">{data.counts?.[s] || 0}</strong> {s}</span>
        ))}
        {/* iter162 — locale histogram. Only renders when more than EN
            shows up, to keep the row quiet for single-locale inboxes. */}
        {(() => {
          const lc = data.locale_counts || {};
          const codes = Object.keys(lc).filter((c) => (lc[c] || 0) > 0);
          if (codes.length <= 1) return null;
          codes.sort((a, b) => (lc[b] || 0) - (lc[a] || 0));
          return (
            <span
              className="inline-flex items-center gap-1.5"
              data-testid="leads-locale-rollup"
              title="Visitor language at submission — captured from the public form"
            >
              · By language:
              {codes.map((c) => (
                <span
                  key={c}
                  data-testid={`leads-locale-rollup-${c}`}
                  className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-indigo-800"
                >
                  {c} · {lc[c]}
                </span>
              ))}
            </span>
          );
        })()}
        {bandFilter !== "ALL" && (
          <button onClick={() => setBandFilter("ALL")} data-testid="leads-band-clear" className="inline-flex items-center gap-1 rounded-full bg-ek-purple/10 px-2 py-0.5 text-ek-purple hover:bg-ek-purple/20">
            <Filter size={11} /> Showing {bandFilter} only · clear
          </button>
        )}
        {/* Catalogue watchers — one-click segment filter */}
        {(() => {
          const watcherCount = (data.leads || []).filter((l) => l.source === "catalogue_watcher").length;
          if (watcherCount === 0 && sourceFilter !== "catalogue_watcher") return null;
          const active = sourceFilter === "catalogue_watcher";
          return (
            <button
              onClick={() => setSourceFilter(active ? "ALL" : "catalogue_watcher")}
              data-testid="leads-source-watchers"
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 transition ${
                active
                  ? "bg-ek-purple text-white"
                  : "bg-amber-50 text-amber-800 hover:bg-amber-100"
              }`}
              title="Show only leads from the public catalogue watcher form"
            >
              <Filter size={11} /> Catalogue watchers · {watcherCount}
              {active ? " · clear" : ""}
            </button>
          );
        })()}
      </div>

      {/* Leads list */}
      <div className="mt-6 space-y-3">
        {loading ? (
          <p className="text-sm text-ek-ink/65">Loading leads…</p>
        ) : data.leads.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-black/10 bg-white py-16 text-center" data-testid="leads-empty">
            <Inbox size={36} className="text-ek-ink/30" />
            <p className="text-sm text-ek-ink/65">No leads yet. They&apos;ll appear here as prospects request demos or the brochure.</p>
          </div>
        ) : (
          <LeadsList
            leads={data.leads.filter((l) =>
              (bandFilter === "ALL" || l.intent?.band === bandFilter) &&
              (sourceFilter === "ALL" || l.source === sourceFilter)
            )}
            onUpdate={updateStatus}
            onProvision={provisionFromLead}
          />
        )}
      </div>
      </main>
    </div>
  );
}


function LeadsList({ leads, onUpdate, onProvision }) {
  const { visible, Toggle } = useCollapsible(leads, 5, "leads");
  const [provisionResults, setProvisionResults] = useState({});
  const [welcomeOptIn, setWelcomeOptIn] = useState({});
  const [busy, setBusy] = useState(null);

  const runProvision = async (lead) => {
    if (busy) return;
    const sendWelcomeEmail = !!welcomeOptIn[lead.lead_id];
    const ok = window.confirm(
      `Provision a new tenant from this lead?\n\n` +
      `Company: ${lead.company || lead.email}\n` +
      `Admin email: ${lead.email}\n` +
      (lead.requested_connector ? `Connector to enable: ${lead.requested_connector}\n` : "") +
      `Infrastructure tier: T1 (Shared SaaS)\n` +
      (sendWelcomeEmail
        ? `\nA welcome email with login link + temp password will be sent to ${lead.email}.`
        : `\nA temporary admin password will be generated for you to send manually.`)
    );
    if (!ok) return;
    setBusy(lead.lead_id);
    try {
      const result = await onProvision(lead.lead_id, { sendWelcomeEmail });
      setProvisionResults((m) => ({ ...m, [lead.lead_id]: result }));
    } catch {
      // toast already surfaced upstream
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {visible.map((l) => {
            const isSnoozed = !!l.roi_signal && (l.status === "contacted" || l.status === "qualified" || l.status === "won");
            const isWarmActive = !!l.roi_signal && !isSnoozed;
            const band = l.intent?.band || "COLD";
            const intentScore = l.intent?.intent_score || 0;
            const bd = l.intent?.breakdown || {};
            const ctx = l.context || {};
            return (
            <div key={l.lead_id} data-testid={`lead-row-${l.lead_id}`}
              className={`rounded-xl border p-4 transition ${band === "HOT" ? "border-rose-300 bg-gradient-to-br from-rose-50/40 to-white" : isWarmActive ? "border-amber-200 bg-gradient-to-br from-amber-50/60 to-white" : isSnoozed ? "border-slate-200 bg-slate-50/60" : "border-black/5 bg-white"}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span data-testid={`lead-intent-${l.lead_id}`}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold border ${BAND_STYLE[band]}`}
                          title={`source ${bd.source||0} + role ${bd.role||0} + recency ${bd.recency||0} + roi ${bd.roi||0} + quality ${bd.quality||0}`}>
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${BAND_DOT[band]}`} />
                      {band} · {intentScore}
                    </span>
                    <p className="font-semibold text-ek-ink">{l.name || l.email}</p>
                    <span className="rounded-full bg-ek-canvas px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide text-ek-ink/65">
                      {l.source || l.kind || "lead"}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[0.65rem] font-medium capitalize ${STATUS_STYLE[l.status] || ""}`}>
                      {l.status}
                    </span>
                    {l.roi_signal && (
                      <span data-testid={`lead-warmth-${l.lead_id}`}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${isSnoozed ? "bg-slate-200 text-slate-500" : "bg-amber-100 text-amber-800"}`}>
                        <Flame size={11} /> {l.roi_signal.source === "one-pager" ? "Downloaded PDF" : "≥£1m estimate"} · {gbp(l.roi_signal.annual_value)}
                      </span>
                    )}
                    {ctx.overall_level && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[0.65rem] font-semibold text-violet-800" title="Self-scored on a public lead magnet">
                        Self-scored L{ctx.overall_level}
                      </span>
                    )}
                    <DeliveryPill lead={l} />
                    <ConnectorMatchPill lead={l} />
                    <EkselensHubBadge lead={l} />
                    {l.locale && l.locale !== "en" && (
                      <span
                        data-testid={`lead-locale-${l.lead_id}`}
                        title={`Visitor was on the ${l.locale.toUpperCase()} locale when they submitted`}
                        className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-indigo-800"
                      >
                        {l.locale}
                      </span>
                    )}
                    {l.tenant_provisioned && (
                      <span
                        data-testid={`lead-tenant-provisioned-${l.lead_id}`}
                        title={`Provisioned ${new Date(l.tenant_provisioned.at).toLocaleString()}`}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[0.65rem] font-semibold text-emerald-800"
                      >
                        <Rocket size={11} /> Tenant {l.tenant_provisioned.tenant_id}
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ek-ink/60">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-ek-purple">
                      <Mail size={13} /> {l.email}
                    </a>
                    <span className="inline-flex items-center gap-1.5">
                      <Building2 size={13} /> {l.company || "—"}{l.role ? ` · ${l.role}` : ""}
                    </span>
                  </div>
                  {l.message && <p className="mt-2 max-w-2xl text-sm text-ek-ink/70">{l.message}</p>}
                  {l.roi_signal && (
                    <p className={`mt-2 text-xs ${isSnoozed ? "text-slate-500" : "text-amber-900/80"}`} data-testid={`lead-warmth-detail-${l.lead_id}`}>
                      On their own figures: {gbp(l.roi_signal.revenue)} revenue · {(l.roi_signal.suppliers || 0).toLocaleString()} suppliers · {l.roi_signal.incidents} incidents/yr.
                    </p>
                  )}
                  {l.roi_signal && (
                    <a
                      href={buildRoiReplyMailto(l)}
                      data-testid={`lead-reply-roi-${l.lead_id}`}
                      className={`mt-2 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium shadow-sm transition ${isSnoozed ? "bg-slate-300 text-slate-700 hover:bg-slate-400" : "bg-amber-600 text-white hover:bg-amber-700"}`}
                    >
                      <Mail size={12} /> {isSnoozed ? "Send another reply" : "Reply with their ROI numbers"}
                    </a>
                  )}
                  <p className="mt-1.5 text-xs text-ek-ink/35">
                    {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}{l.source ? ` · ${l.source}` : ""}
                  </p>
                  {l.source === "catalogue_watcher" && !l.tenant_provisioned && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => runProvision(l)}
                        disabled={busy === l.lead_id}
                        data-testid={`lead-provision-${l.lead_id}`}
                        className="inline-flex items-center gap-1.5 rounded-md bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-ek-purple/90 disabled:opacity-50"
                      >
                        {busy === l.lead_id ? (
                          <><RefreshCw size={12} className="animate-spin" /> Provisioning…</>
                        ) : (
                          <><Rocket size={12} /> Provision tenant{l.requested_connector ? ` (enable ${l.requested_connector})` : ""}</>
                        )}
                      </button>
                      <label
                        className="inline-flex items-center gap-1.5 text-xs text-ek-ink/65 cursor-pointer"
                        data-testid={`lead-provision-welcome-toggle-${l.lead_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!welcomeOptIn[l.lead_id]}
                          onChange={(e) =>
                            setWelcomeOptIn((m) => ({ ...m, [l.lead_id]: e.target.checked }))
                          }
                          className="h-3.5 w-3.5 rounded border-black/20 text-ek-purple focus:ring-ek-purple"
                        />
                        <Mail size={11} /> Send welcome email
                      </label>
                    </div>
                  )}
                  {provisionResults[l.lead_id] && (
                    <ProvisionResultPanel
                      result={provisionResults[l.lead_id]}
                      onDismiss={() => setProvisionResults((m) => {
                        const c = { ...m }; delete c[l.lead_id]; return c;
                      })}
                    />
                  )}
                </div>
                <div className="w-36">
                  <Select value={l.status} onValueChange={(v) => onUpdate(l.lead_id, v)}>
                    <SelectTrigger data-testid={`lead-status-${l.lead_id}`} className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            );
          })}
      <Toggle className="mt-3" />
    </>
  );
}


/**
 * ProvisionResultPanel — inline result block shown after a successful
 * one-click tenant provision. Surfaces the new tenant_id + the one-time
 * admin password (copy-to-clipboard) so the Owner can paste both into a
 * welcome email without ever leaving the Leads page.
 */
function ProvisionResultPanel({ result, onDismiss }) {
  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Copy failed — ${label} is shown below.`);
    }
  };
  return (
    <div
      data-testid={`lead-provision-result-${result.lead?.lead_id || result.tenant_id}`}
      className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 text-xs"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="font-semibold text-emerald-900">
          Tenant provisioned · {result.tenant_id}
        </p>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded p-0.5 text-emerald-900/60 hover:bg-emerald-100"
          data-testid={`lead-provision-dismiss-${result.tenant_id}`}
          aria-label="Dismiss"
        >
          <X size={13} />
        </button>
      </div>
      <dl className="grid grid-cols-[auto,1fr,auto] gap-x-3 gap-y-1.5 text-emerald-900/80">
        <dt className="font-medium">Admin email</dt>
        <dd className="font-mono">{result.admin_email}</dd>
        <dd>
          <button
            type="button"
            onClick={() => copy("Admin email", result.admin_email)}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-emerald-700 hover:bg-emerald-100"
          >
            <Copy size={11} /> Copy
          </button>
        </dd>

        <dt className="font-medium">Temp password</dt>
        <dd className="font-mono">{result.admin_password}</dd>
        <dd>
          <button
            type="button"
            onClick={() => copy("Password", result.admin_password)}
            data-testid={`lead-provision-copy-pwd-${result.tenant_id}`}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-emerald-700 hover:bg-emerald-100"
          >
            <Copy size={11} /> Copy
          </button>
        </dd>

        <dt className="font-medium">Connectors</dt>
        <dd className="col-span-2">
          {result.configuration?.enabled_connectors?.length
            ? result.configuration.enabled_connectors.join(", ")
            : "all (default)"}
        </dd>

        <dt className="font-medium">Tier</dt>
        <dd className="col-span-2 capitalize">
          {result.configuration?.infrastructure_tier || "standard"}
        </dd>

        {!!(result.starter_pack?.controls || result.starter_pack?.kris) && (
          <>
            <dt className="font-medium">Starter pack</dt>
            <dd className="col-span-2">
              {result.starter_pack.controls} controls · {result.starter_pack.kris} KRIs
            </dd>
          </>
        )}
        {result.welcome_email && result.welcome_email.reason !== "not_requested" && (
          <>
            <dt className="font-medium">Welcome email</dt>
            <dd className="col-span-2" data-testid={`lead-provision-welcome-status-${result.tenant_id}`}>
              {result.welcome_email.sent ? (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 size={11} /> Sent to {result.admin_email}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <AlertTriangle size={11} /> Failed — {result.welcome_email.error || result.welcome_email.reason}
                </span>
              )}
            </dd>
          </>
        )}
      </dl>
      <p className="mt-2 text-[10px] uppercase tracking-wide text-emerald-700/70">
        This password is shown once. Copy it now, paste into your welcome email.
      </p>
    </div>
  );
}



/**
 * Compact deliverability pill — surfaces whether the role-tailored PDF
 * report was actually delivered via Resend after the lead-magnet submission.
 * Renders `Report delivered ✓ at HH:MM` for sent emails, an amber failure
 * pill when delivery failed, and nothing at all when no email was attempted
 * (e.g. plain demo/brochure leads that never produce an automated PDF).
 */
function DeliveryPill({ lead }) {
  const d = lead?.email_delivery;
  if (!d) return null;
  if (d.sent === false) {
    return (
      <span data-testid={`lead-delivery-failed-${lead.lead_id}`}
        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[0.65rem] font-semibold text-amber-800"
        title={d.error || "Resend delivery failed"}>
        <AlertTriangle size={11} /> Report not sent
      </span>
    );
  }
  const ts = d.delivered_at ? new Date(d.delivered_at) : null;
  const hhmm = ts && !isNaN(ts.getTime())
    ? ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";
  return (
    <span data-testid={`lead-delivery-${lead.lead_id}`}
      className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[0.65rem] font-semibold text-emerald-800"
      title={d.email_id ? `Resend id: ${d.email_id}` : "Delivered via Resend"}>
      <CheckCircle2 size={11} /> Report delivered{hhmm ? ` ✓ at ${hhmm}` : ""}
    </span>
  );
}


/**
 * Hot-Leads ↔ Connector Match pill — when this prospect's email domain shows
 * up as an `owner` on any synced PPM plan, surface that signal on the CRM
 * row so reps can prioritise "lead from a customer already running plans"
 * over a cold lead with no upstream footprint.
 */
function ConnectorMatchPill({ lead }) {
  const m = lead?.connector_match;
  if (!m || !m.count) return null;
  const tooltip = (m.by_connector || []).map((c) => `${c.name}: ${c.count}`).join(" · ");
  return (
    <span data-testid={`lead-connector-match-${lead.lead_id}`}
      className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[0.65rem] font-semibold text-violet-800"
      title={`@${m.domain} — ${tooltip}`}>
      <Plug size={11} /> {m.count} live project{m.count === 1 ? "" : "s"}
    </span>
  );
}



/**
 * Ekselens Hub provenance badge — visible when this ERIO lead has been
 * forwarded to the shared Ekselens Lead Hub. Turns the forwarder from
 * "fire-and-forget plumbing" into an auditable provenance trail so the
 * Owner can tell at a glance which leads are in the Hub vs. local-only.
 */
function EkselensHubBadge({ lead }) {
  if (!lead?.ekselens_lead_id) return null;
  const hubId = String(lead.ekselens_lead_id);
  const deduped = lead.ekselens_deduped;
  const tooltipParts = [
    `Ekselens Hub id: ${hubId}`,
    deduped ? "(deduplicated against existing Hub row)" : null,
    lead.ekselens_forwarded_at
      ? `Forwarded ${new Date(lead.ekselens_forwarded_at).toLocaleString()}`
      : null,
  ].filter(Boolean);
  return (
    <span
      data-testid={`lead-ekselens-hub-${lead.lead_id}`}
      title={tooltipParts.join("\n")}
      className="inline-flex items-center gap-1 rounded-full bg-ek-purple/10 px-2 py-0.5 text-[0.65rem] font-semibold text-ek-purple"
    >
      ↗ Hub · {hubId.slice(0, 12)}
      {deduped ? " · dedup" : ""}
    </span>
  );
}
